import * as React from "react";
import {authorize, IWithRouterProps, Overlay, withRouter} from "@athena-portal/components";
import {lazyInject} from "@athena-portal/core";
import {
    IFalconConvenienceApiService,
    resourcesHelper,
    FalconNavigatorHeader,
    ICacheManager,
    ICustomerPortalService,
    Policy,
    ProductsEnum,
    Renewal,
    Subscription,
    Types,
    POLICY_STATUS_MTA,
    PolicyPropertyEnum,
    PolicyProperty, PremiumType, POLICY_STATUS_PENDING_RENEWAL,
} from "@falcon/services";
import {
    allowToRenderFutureMTAExplainedHeader,
    capitalize,
    findLatestNotFutureMTAPolicyVersion, findPreviousPolicyVersionByAgreementVersion, getCoverTierWording,
    getPolicyInteraction
} from "../../../../../CommonFunctions";
import {ButtonLink} from "../../../../../components/navigation/ButtonLink";
import FalconFooter from "../../../../../components/navigation/FalconFooter";
import FalconHeader from "../../../../../components/navigation/FalconHeader";
import {StorageSuccessModal} from "../../../../../components/modals/StorageSuccessModal";
import {PluginTemplates} from "../../../../../utils/PluginTemplates";
import {AnnualYourCarPanel} from "./AnnualYourCarPanel";
import {EditCarMtaSuccessModalWordingGenerator} from "./modal/EditCarMtaSuccessModalWordingGenerator";
import {ReplaceCarMtaSuccessModalWordingGenerator} from "./modal/ReplaceCarMtaSuccessModalWordingGenerator";
import {RemoveDriverMtaSuccessModalWordingGenerator} from "./modal/RemoveDriverMtaSuccessModalWordingGenerator";
import {EditDriverMtaSuccessModalWordingGenerator} from "./modal/EditDriverMtaSuccessModalWordingGenerator";
import {AddDriverMtaSuccessModalWordingGenerator} from "./modal/AddDriverMtaSuccessModalWordingGenerator";
import {FutureMTAExplainedHeader} from "./FutureMTAExplainedHeader";
import {MtaLimitWarningBanner} from "../../../../../components/banners/MtaLimitWarningBanner";
import * as moment from "moment";
import {UpdateOtherPolicesBanner} from "../common/UpdateOtherPolicesBanner";
import {pushToAdobeDataLayerDelayed} from "../../../../../utils/AdobeDataLayerHelper";
import {EditCoverLevelMtaSuccessModalWordingGenerator} from "./modal/EditCoverLevelMtaSuccessModalWordingGenerator";
import {AllianzOnlineBadge} from "../../../../../components/AllianzOnlineBadge";
import {RenewalChangesBanner} from "../common/RenewalChangesBanner";

interface IYourCarsPageParams {
    policyNumber: string;
    [key: string]: string | undefined
}

interface IWolfYourCarsPageState {
    isLoading: boolean;
    annualMotorPolicies: Policy[];
    allPolicies: Policy[];
    subscription?: Subscription;
    renewalProposal?: Renewal;
    previousMonthlyPremium?: number;
    renewalEnabled: boolean;
    previousCoverTier?: string
    allianzDirectClaimsEnabled: boolean,
}

interface ISettings {
    allianzDirectClaimsEnabled?: string | boolean;
}

const isSettingEnabled = (value: string | boolean | undefined): boolean => value === true || value === "true";

const buildDigitalClaimsUrl = (baseUrl: string | undefined, policyNumber: string): string | undefined => {
    if (!baseUrl) {
        return undefined;
    }

    const separator = baseUrl.includes("?") ? "&" : "?";

    return `${baseUrl}${separator}journey=digital&policy=${encodeURIComponent(policyNumber)}`;
};

@authorize
export class AnnualYourCarsPagePlain extends React.Component<IWithRouterProps<IYourCarsPageParams>, IWolfYourCarsPageState> {
    private pluginTemplates = new PluginTemplates();

    @lazyInject(Types.CustomerPortalService)
    private customerPortalService: ICustomerPortalService;

    @lazyInject(Types.CacheManager)
    private cacheManager: ICacheManager;

    @lazyInject(Types.FalconConvenienceApiService)
    private readonly falconConvenienceApiService: IFalconConvenienceApiService;

    private pushToAdobeDataLayerTimeout: any;

    constructor(props: IWithRouterProps<IYourCarsPageParams>) {
        super(props);

        this.state = {
            isLoading: true,
            annualMotorPolicies: [],
            allPolicies: [],
            renewalProposal: undefined,
            renewalEnabled: false,
            allianzDirectClaimsEnabled: false,
        };
    }

    public componentDidMount(): void {
        this.loadAllianzDirectClaimsSwitch();

        this.pluginTemplates.setPageTitle("Your cars");

        // In case this page was navigated to from a successful MTA, ensure that the most recent policy version is loaded
        if (window.sessionStorage.getItem("showPopup") === "true") {
            this.cacheManager.clearCache("getPolicies");
        }

        this.setState({isLoading: true});
        const policyNumber = this.props.params.policyNumber;

        this.customerPortalService
            .getPolicies(ProductsEnum.WOLF_MOTOR)
            .subscribe((policies: Policy[]) => {
                const filteredPolicies: Policy[] = policies
                    .filter((policy: Policy) => policy.policyNumber === policyNumber)
                    .sort((a, b) => moment(b.endDate).diff(a.endDate));

                this.setState({annualMotorPolicies: filteredPolicies || [],});

                if (policies && policies.length > 0) {

                    this.customerPortalService.getRenewalProposal(policies[0].policyNumber).subscribe(
                        (renewalProposal: Renewal) => {
                            this.setState({
                                renewalProposal
                            })
                        }
                    );

                    this.customerPortalService.getSubscription()
                        .subscribe((subscription: Subscription) => {
                            this.setState({subscription});
                        }
                    );

                    this.customerPortalService.getPolicyPropertyByPropertyKey(this.props.params.policyNumber, PolicyPropertyEnum.PERFORM_RENEWAL).subscribe(
                        (property: PolicyProperty) => {
                            this.setState({
                                renewalEnabled: property.value === "true"
                            });
                        }
                    );

                    this.customerPortalService.getPolicyHistory().subscribe(
                        (allPolicies: Policy[]) => {
                            const previousPolicyVersion = findPreviousPolicyVersionByAgreementVersion(allPolicies, policyNumber);
                            this.setState({
                                annualMotorPolicies: filteredPolicies,
                                allPolicies,
                                isLoading: false
                            });

                            if (previousPolicyVersion) {
                                const previousInstalments = previousPolicyVersion.interaction.paymentDetails.instalmentPlan.instalments[0];
                                const previousMonthlyPremium = previousInstalments ? previousInstalments.principal + previousInstalments.interest : 0;
                                const previousCoverTier = previousPolicyVersion.interaction.coverTier;
                                this.setState({previousMonthlyPremium, previousCoverTier});
                            }
                        }
                    );
                }
            });

        this.pushToAdobeDataLayerTimeout = pushToAdobeDataLayerDelayed(2000);
    }

    componentWillUnmount() {
        clearTimeout(this.pushToAdobeDataLayerTimeout);
    }

    public render() {
        if (this.state.annualMotorPolicies && this.state.annualMotorPolicies.length > 0 && !this.state.isLoading) {
            const policy: Policy = this.state.annualMotorPolicies[0];
            const hasPendingRenewal: boolean = this.state.renewalProposal != null && this.state.renewalProposal.status === POLICY_STATUS_PENDING_RENEWAL;

            return (
                <>
                    <FalconHeader/>
                    <div className="page-contents">
                        <AllianzOnlineBadge/>
                        <FalconNavigatorHeader
                            back={{
                                link: `/dashboard/annualMotor/${this.props.params.policyNumber}`,
                            }}
                            title="Your car, make changes"
                        />

                        {this.state.annualMotorPolicies.length > 2 &&
                            <UpdateOtherPolicesBanner policyNumber={this.props.params.policyNumber}/>}
                        {hasPendingRenewal && <RenewalChangesBanner/>}
                        {
                            this.state.annualMotorPolicies && allowToRenderFutureMTAExplainedHeader(policy.interaction.process) &&
                            <FutureMTAExplainedHeader
                                policyNumber={this.props.params.policyNumber}
                                policyEndDate={policy.endDate}
                                upcomingMtaDate={policy.annualPolicyInfo.maxFutureMtaDate}
                                isEligibleForEnhancedCourtesyCar={policy.interaction.uistate.isEligibleForEnhancedCourtesyCar}
                            />
                        }
                        {this.renderPageContents()}
                        {this.pluginTemplates.renderBackLink("Back", false, `/dashboard/annualMotor/${this.props.params.policyNumber}`)}
                    </div>
                    <FalconFooter/>
                </>
            );
        } else {
            return (<Overlay show={true}/>);
        }
    }

    private renderPageContents = () => {
        if (this.state.isLoading) {
            return <Overlay show={true}/>;
        }

        const showSuccessModal = window.sessionStorage.getItem("showPopup") === "true";
        const showGreenCardHeadsUpBanner = window.sessionStorage.getItem("referrerJourney") === "greenCard";
         const defaultClaimsLink = "/dashboard/pickClaimKind";

        const motorDirectClaimsLink = buildDigitalClaimsUrl(
            process.env.REACT_APP_MOTOR_OTHER_CLAIM_URL,
            this.props.params.policyNumber
        );

        const useHomeDirectClaimsLink = resourcesHelper.THEME !== "Britannia" && this.state.allianzDirectClaimsEnabled && motorDirectClaimsLink != null;

        const claimsLink = useHomeDirectClaimsLink && motorDirectClaimsLink ? motorDirectClaimsLink : defaultClaimsLink
        return (
            <>
                {this.state.annualMotorPolicies.length > 0 && this.state.annualMotorPolicies[0].interaction &&
                    <MtaLimitWarningBanner interaction={this.state.annualMotorPolicies[0].interaction}/>}
                {showGreenCardHeadsUpBanner && this.renderGreenCardHeadsUpBanner()}
                <div className="page-contents-inner">
                    {showSuccessModal && this.renderSuccessModal()}
                    {this.renderYourCarCoverPanels()}

                    <ButtonLink id="make-claim" primary={true} to={claimsLink} className="make-claim">
                        Make a claim
                    </ButtonLink>
                </div>
            </>
        );
    };

    private readonly renderSuccessModal = (): React.ReactNode => {
        const successModalReferrer = window.sessionStorage.getItem("referrerJourney");
        switch (successModalReferrer) {
            case "mtaEditVehicleDetails":
                return this.renderMtaEditVehicleDetailsSuccessModal();
            case "mtaRemoveDriver":
                return this.renderMtaRemoveDriverSuccessModal();
            case "replaceCar":
                return this.renderMtaReplaceCarSuccessModal();
            case "mtaEditDriverDetails":
                return this.renderMtaEditDriverDetailsSuccessModal();
            case "greenCard":
                return this.renderGreenCardRequestSuccessModal();
            case "mtaAddPermanentDriver":
                return this.renderAddAdditionalDriverSuccessModal();
            case "mtaEditVehicleRegistrationNumber":
                return this.renderEditRegistrationNumberSuccessModal();
            case "mtaEditCoverLevel":
                return this.renderEditCoverLevelSuccessModal();
            case "mtaChangeAddressAnnualMotor":
                return this.renderChangeAddressSuccessModal();
            default:
                return null;
        }
    };

    private readonly renderMtaEditVehicleDetailsSuccessModal = (): React.ReactNode => {

        const interaction = this.state.annualMotorPolicies[0].interaction;

        const {factors, uistate, paymentDetails, policyHolder, agreeToReuseCardDetails} = interaction;

        const mtaSuccessMessageText = window.sessionStorage.getItem("mtaSuccessMessageText");

        const wording = EditCarMtaSuccessModalWordingGenerator.generateModalWording(
            factors, paymentDetails, uistate.payFullPrice, mtaSuccessMessageText,
            policyHolder.cardDetails?.at(-1)?.number, agreeToReuseCardDetails, this.state.previousMonthlyPremium);

        window.sessionStorage.removeItem("mtaHeadsUpMessageText");

        return (
            <StorageSuccessModal
                title={wording.modalTitle}
                explanationWording={
                    <p id="mtaPaymentSuccessful">
                        {wording.modalBody}
                    </p>
                }
                anythingElseTitle={wording.modalAnythingElseChangedTitle}
                explanationAnythingElseWording={
                    <p id="mtaPaymentSuccessfulAnythingElse">
                        {wording.modalAnythingElseChangedBody}
                    </p>
                }
            />
        );
    };

    private readonly renderEditCoverLevelSuccessModal = (): React.ReactNode => {

        const interaction = this.state.annualMotorPolicies[0].interaction;

        const {factors, uistate, paymentDetails, policyHolder, agreeToReuseCardDetails} = interaction;

        const mtaSuccessMessageText = `for changing cover level from ${getCoverTierWording(this.state.previousCoverTier)} to ${getCoverTierWording(interaction.coverTier)}`;

        const wording = EditCoverLevelMtaSuccessModalWordingGenerator.generateModalWording(
            factors, paymentDetails, uistate.payFullPrice, mtaSuccessMessageText,
            policyHolder.cardDetails?.at(-1)?.number, agreeToReuseCardDetails, this.state.previousMonthlyPremium);

        window.sessionStorage.removeItem("mtaHeadsUpMessageText");

        return (
            <StorageSuccessModal
                title={wording.modalTitle}
                explanationWording={
                    <p id="mtaPaymentSuccessful">
                        {wording.modalBody}
                    </p>
                }
                anythingElseTitle={wording.modalAnythingElseChangedTitle}
                explanationAnythingElseWording={
                    <p id="mtaPaymentSuccessfulAnythingElse">
                        {wording.modalAnythingElseChangedBody}
                    </p>
                }
            />
        );
    };

    private readonly renderMtaRemoveDriverSuccessModal = (): React.ReactNode => {
        const interaction = this.state.annualMotorPolicies[0].interaction;

        const {factors, uistate} = interaction;
        const instalmentsNotPaidOff: boolean = interaction.factors.premiumType === PremiumType.monthly && interaction.paymentDetails.instalmentPlan.totalAmount > 0;

        const wording = RemoveDriverMtaSuccessModalWordingGenerator.generateModalWording(factors, uistate,
            interaction.policyHolder.cardDetails?.at(-1)?.number, interaction.agreeToReuseCardDetails, this.state.previousMonthlyPremium, instalmentsNotPaidOff);

        return (
            <StorageSuccessModal
                title={wording.modalTitle}
                explanationWording={<p id = "mtaPaymentSuccessful">{wording.modalBody} </p>}
                anythingElseTitle={wording.modalAnythingElseChangedTitle}
                explanationAnythingElseWording={
                    <p id="mtaPaymentSuccessfulAnythingElse">
                        {wording.modalAnythingElseChangedBody}
                    </p>
                 }
            />
        );
    };

    private readonly renderMtaReplaceCarSuccessModal = (): React.ReactNode => {
        const interactionId = window.sessionStorage.getItem("sessionInteraction");
        const interaction = getPolicyInteraction(interactionId, this.state.annualMotorPolicies);

        const newVehicle = interaction.vehicles[0];
        const oldVehicle = {
            registrationNumber: window.sessionStorage.getItem("oldCarVrn"),
            make: window.sessionStorage.getItem("oldCarMake"),
            model: window.sessionStorage.getItem("oldCarModel")
        };

        const {factors, uistate} = interaction;
        const instalmentsNotPaidOff: boolean = interaction.factors.premiumType === PremiumType.monthly && interaction.paymentDetails.instalmentPlan.totalAmount > 0;

        const wording = ReplaceCarMtaSuccessModalWordingGenerator.generateModalWording(oldVehicle, newVehicle, factors, uistate.payFullPrice,
            interaction.policyHolder.cardDetails?.at(-1)?.number, interaction.agreeToReuseCardDetails, interaction, this.state.previousMonthlyPremium, instalmentsNotPaidOff);

        return (
            <StorageSuccessModal
                title={wording.modalTitle}
                explanationWording={<p id = "mtaPaymentSuccessful">{wording.modalBody} </p>}
                anythingElseTitle={wording.modalAnythingElseChangedTitle}
                explanationAnythingElseWording={
                    <p id="mtaPaymentSuccessfulAnythingElse">
                        {wording.modalAnythingElseChangedBody}
                    </p>
                }
            />
        );
    };

    private readonly renderMtaEditDriverDetailsSuccessModal = (): React.ReactNode => {
        const interactionId = window.sessionStorage.getItem("sessionInteraction");
        const interaction = getPolicyInteraction(interactionId, this.state.annualMotorPolicies);

        const {factors, uistate} = interaction;
        const driverIndicator = window.sessionStorage.getItem("mtaEditDriverDetailsDriver") || "";
        const driver = driverIndicator === "policyHolder" ? interaction.policyHolder : interaction.additionalDrivers[parseInt(driverIndicator, 10)];
        const instalmentsNotPaidOff: boolean = interaction.factors.premiumType === PremiumType.monthly && interaction.paymentDetails.instalmentPlan.totalAmount > 0;

        const wording = EditDriverMtaSuccessModalWordingGenerator.generateModalWording(factors, driver, uistate.payFullPrice,
            interaction.policyHolder.cardDetails?.at(-1)?.number, interaction.agreeToReuseCardDetails, this.state.previousMonthlyPremium, instalmentsNotPaidOff);

        return (
            <StorageSuccessModal
                title={wording.modalTitle}
                explanationWording={<p id = "mtaPaymentSuccessful">{wording.modalBody} </p>}
                anythingElseTitle={wording.modalAnythingElseChangedTitle}
                explanationAnythingElseWording={
                    <p id="mtaPaymentSuccessfulAnythingElse">
                        {wording.modalAnythingElseChangedBody}
                    </p>
                }
            />
        );
    };

    private readonly renderGreenCardRequestSuccessModal = (): React.ReactNode => {
        const greenCardVrn = window.sessionStorage.getItem("greenCardVrn");
        const greenCardCaravanOrTrailerId = window.sessionStorage.getItem("greenCardCaravanVrn");

        const policy = this.state.annualMotorPolicies.find(p => p.insuredVehicles[0].registrationNumber === greenCardVrn);
        if (!policy) {
            return;
        }

        const vehicle = policy.insuredVehicles[0];

        const modalTitle = "We’ve received your Green Card request";
        const modalBody = (
            <p className="font-weight-extra-light">
                Your Green Card
                for {capitalize(vehicle.make)}, {capitalize(vehicle.model)} ({vehicle.registrationNumber}){" "}
                {greenCardCaravanOrTrailerId && <>and Trailer or Caravan ({greenCardCaravanOrTrailerId})</>}{" "}
                will be available in ‘Your documents’ for you to print at home on white paper. Remember to carry it with
                you on the trip with your other documents
            </p>
        );

        return (
            <StorageSuccessModal
                title={modalTitle}
                explanationWording={<p id = "mtaPaymentSuccessful">{modalBody} </p>}
                anythingElseTitle={EditCarMtaSuccessModalWordingGenerator.generateAnythingElseChangedModalTitle()}
                explanationAnythingElseWording={<p id = "mtaPaymentSuccessfulAnythingElse">{EditCarMtaSuccessModalWordingGenerator.generateAnythingElseChangedModalBody()}</p>}
            />
        );
    };

    private readonly renderChangeAddressSuccessModal = (): React.ReactNode => {

        const interaction = this.state.annualMotorPolicies[0].interaction;

        const {factors, uistate, paymentDetails, policyHolder, agreeToReuseCardDetails} = interaction;

        const mtaSuccessMessageText = "for changing address";

        const wording = EditCarMtaSuccessModalWordingGenerator.generateModalWording(
            factors, paymentDetails, uistate.payFullPrice, mtaSuccessMessageText,
            policyHolder.cardDetails?.at(-1)?.number, agreeToReuseCardDetails, this.state.previousMonthlyPremium);

        return (
            <StorageSuccessModal
                title={wording.modalTitle}
                explanationWording={
                    <p id="mtaPaymentSuccessful">
                        {wording.modalBody}
                    </p>
                }
                anythingElseTitle={wording.modalAnythingElseChangedTitle}
                explanationAnythingElseWording={
                    <p id="mtaPaymentSuccessfulAnythingElse">
                        {wording.modalAnythingElseChangedBody}
                    </p>
                }
            />
        );
    };

    private renderYourCarCoverPanels(): React.ReactNode {
        const {annualMotorPolicies, allPolicies, subscription} = this.state;

        if (!(annualMotorPolicies && allPolicies && subscription)) {
            return;
        }

        return annualMotorPolicies!.map((policy: Policy, index: number) => {
            let currentlyActivePolicy: Policy | undefined;
            // If there is a future MTA, fetch latest policy before future MTA.
            if (policy.status === POLICY_STATUS_MTA && moment().isBefore(policy.interaction.headerInfo.startDate)) {
                currentlyActivePolicy = findLatestNotFutureMTAPolicyVersion(this.state.allPolicies!, policy.policyNumber);
            }

            return (
                <>
                    <AnnualYourCarPanel
                        key={index}
                        interaction={policy.interaction}
                        currentlyActiveInteraction={currentlyActivePolicy?.interaction}
                        vehicleIndex={index}
                        subscription={subscription!}
                        annualPolicyInfo={policy.annualPolicyInfo!}
                        policyEndDate={policy.endDate}
                    />

                    {this.renderCancelPolicyButton(policy, index)}
                </>
            );
        });
    }

    private readonly renderGreenCardHeadsUpBanner = (): React.ReactNode => {
        const greenCardVrn = window.sessionStorage.getItem("greenCardVrn");
        const greenCardCaravanOrTrailerId = window.sessionStorage.getItem("greenCardCaravanVrn");

        const policy = this.state.annualMotorPolicies.find(p => p.insuredVehicles[0].registrationNumber === greenCardVrn);
        if (!policy) {
            return;
        }

        const vehicle = policy.insuredVehicles[0];

        return (
            <div className="banner">
                <div className="banner-text banner-info">
                    Heads up - You just requested a Green Card for {capitalize(vehicle.make)},{" "}
                    {capitalize(vehicle.model)} ({vehicle.registrationNumber}){" "}
                    {greenCardCaravanOrTrailerId && <>and Trailer or Caravan ({greenCardCaravanOrTrailerId})</>}
                </div>
            </div>
        );
    };

    private readonly renderAddAdditionalDriverSuccessModal = (): React.ReactNode => {
        const interactionId = window.sessionStorage.getItem("sessionInteraction");
        const interaction = getPolicyInteraction(interactionId, this.state.annualMotorPolicies);
        const {factors, uistate} = interaction;

        if (interaction.additionalDrivers.length === 0) {

            return;
        }

        const newAdditionalDriversCount: number = interaction.additionalDrivers.length - parseInt(window.sessionStorage.getItem("initialAdditionalDriversNumber")!, 10);
        const additionalDrivers: any = interaction.additionalDrivers.slice(-newAdditionalDriversCount);

        const car: any = interaction.vehicles[0];
        const instalmentsNotPaidOff: boolean = interaction.factors.premiumType === PremiumType.monthly && interaction.paymentDetails.instalmentPlan.totalAmount > 0;
        const conditions = {
            agreeToReuseCardDetails : interaction.agreeToReuseCardDetails,
            instalmentsNotPaidOff
        }
        const wording = AddDriverMtaSuccessModalWordingGenerator.generateModalWording(factors, uistate, car, additionalDrivers,
            interaction.policyHolder.cardDetails?.at(-1)?.number, conditions, this.state.previousMonthlyPremium);

        return (
            <StorageSuccessModal
                title={wording.modalTitle}
                explanationWording={<p id = "mtaPaymentSuccessful">{wording.modalBody} </p>}
                anythingElseTitle={wording.modalAnythingElseChangedTitle}
                explanationAnythingElseWording={
                    <p id="mtaPaymentSuccessfulAnythingElse">
                        {wording.modalAnythingElseChangedBody}
                    </p>
                }
            />
        );
    };

    private readonly renderEditRegistrationNumberSuccessModal = (): React.ReactNode => {
        const modalTitle = "Change was successful";

        const vehicle = this.state.annualMotorPolicies[0].interaction.vehicles[0];
        const modalBody = (
            <p className="font-weight-extra-light">
                You have changed the registration number of this{" "}
                {vehicle.make}, {vehicle.model}, {vehicle.year}{" "}
                to <span className="font-weight-bold">{vehicle.registrationNumber}</span>
            </p>
        );

        return (
            <StorageSuccessModal
                title={modalTitle}
                explanationWording={<p id = "mtaPaymentSuccessful">{modalBody} </p>}
                anythingElseTitle={EditCarMtaSuccessModalWordingGenerator.generateAnythingElseChangedModalTitle()}
                explanationAnythingElseWording={<p id="mtaPaymentSuccessfulAnythingElse">{EditCarMtaSuccessModalWordingGenerator.generateAnythingElseChangedModalBody()}</p>}
            />
        );
    };

    private renderCancelPolicyButton(policy: Policy, index: number): React.ReactNode {
        const hasPendingRenewal: boolean = this.state.renewalProposal != null && this.state.renewalProposal.status === POLICY_STATUS_PENDING_RENEWAL;
        if (hasPendingRenewal) {
            return (
                <ButtonLink
                    key={index}
                    id={`cancel-policy${index}`}
                    className="cancel-policy"
                    primary={false}
                    to={`/dashboard/annualMotor/cancel/${policy.policyNumber}`}
                    routeState={{
                        isRenewalCancellation: true,
                        renewalCancellationUrl: '/journey/init/product/AnnualMotorInsurance/process/renewal/renewalMTCEntryPage/' + this.state.renewalProposal!._id
                    }}
                    disabled={this.state.subscription ? this.state.subscription.allAnnualMotorPoliciesAreCancelled : false}
                >
                    Cancel policy
                </ButtonLink>
            )
        } else {
            return (
                <ButtonLink
                    key={index}
                    id={`cancel-policy${index}`}
                    className="cancel-policy"
                    primary={false}
                    to={`/journey/init/product/AnnualMotorInsurance/process/mtc/presetPage/${policy.interaction?._id}`}
                    disabled={this.state.subscription ? this.state.subscription.allAnnualMotorPoliciesAreCancelled : false}
                >
                    Cancel policy
                </ButtonLink>
            )
        }
    }

    private loadAllianzDirectClaimsSwitch(): void {
        this.falconConvenienceApiService
            .getSettings()
            .subscribe((settings: ISettings): void => {
                console.log("settings", settings)
                this.setState({
                    allianzDirectClaimsEnabled: isSettingEnabled(settings?.allianzDirectClaimsEnabled)
                });
            });
    }
}

export const AnnualYourCarsPage = withRouter<{}, IYourCarsPageParams>(AnnualYourCarsPagePlain)
